import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { formatBirthDateDisplayUtc } from '../../utils/dateInput';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import { FaEye, FaCheck, FaFilePdf, FaTrash } from 'react-icons/fa';
import './AdminPages.css';

const AdminJuventusCard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/juventus-card-requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching Juventus Card requests:', error);
      alert('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/juventus-card-requests/${id}`, { status });
      alert('Statut mis à jour');
      fetchRequests();
      if (selected?._id === id) setSelected({ ...selected, status });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleView = (req) => {
    setSelected(req);
    setShowModal(true);
  };

  const handleDelete = async (req) => {
    if (
      !window.confirm(
        `Supprimer définitivement la demande de ${req.firstName} ${req.lastName} (${req.email}) ?`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/juventus-card-requests/${req._id}`);
      alert('Demande supprimée');
      if (selected?._id === req._id) {
        setShowModal(false);
        setSelected(null);
      }
      fetchRequests();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPdf = async (req) => {
    try {
      const response = await api.get(`/juventus-card-requests/${req._id}/pdf`, { responseType: 'blob' });
      const contentType = response.headers['content-type'] || '';
      if (response.status !== 200 || contentType.includes('application/json')) {
        const text = await response.data.text();
        let msg = 'Erreur lors du téléchargement du PDF';
        try {
          const json = JSON.parse(text);
          if (json.message) msg = json.message;
        } catch (_) {}
        alert(msg);
        return;
      }
      const disposition = response.headers['content-disposition'];
      let filename = `juventus-card-${req.lastName}-${req.firstName}.pdf`;
      if (disposition) {
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        if (match) filename = match[1].trim();
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filtered = filterStatus === 'all'
    ? requests
    : requests.filter(r => r.status === filterStatus);

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1>Demandes Juventus Card</h1>
          <div className="header-actions">
            <button className="btn-back" onClick={() => navigate('/admin')}>
              ← Retour
            </button>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', fontSize: '14px', marginLeft: '10px' }}
            >
              <option value="all">Tous</option>
              <option value="pending">En attente</option>
              <option value="processed">Traitées</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Langue</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                      Aucune demande
                    </td>
                  </tr>
                ) : (
                  filtered.map((req) => (
                    <tr key={req._id}>
                      <td>{formatDate(req.createdAt)}</td>
                      <td>{req.title} {req.firstName} {req.lastName}</td>
                      <td>{req.email}</td>
                      <td>{req.formLanguage || 'fr'}</td>
                      <td>
                        <span className={`status status-${req.status === 'processed' ? 'confirmed' : 'pending'}`}>
                          {req.status === 'processed' ? 'Traité' : 'En attente'}
                        </span>
                      </td>
                      <td className="actions">
                        <button className="btn-view" onClick={() => handleView(req)} title="Voir">
                          <FaEye />
                        </button>
                        <button
                          className="btn-pdf"
                          onClick={() => handleDownloadPdf(req)}
                          title="Télécharger PDF"
                        >
                          <FaFilePdf />
                        </button>
                        {req.status === 'pending' && (
                          <button
                            className="btn-edit"
                            onClick={() => handleStatusChange(req._id, 'processed')}
                            title="Marquer comme traité"
                          >
                            <FaCheck />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-delete"
                          title="Supprimer"
                          aria-label="Supprimer"
                          onClick={() => handleDelete(req)}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && selected && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Demande Juventus Card – {selected.firstName} {selected.lastName}</h2>
                <div className="modal-header-actions">
                  <button
                    type="button"
                    className="btn-download-pdf"
                    onClick={() => handleDownloadPdf(selected)}
                    title="Télécharger PDF"
                  >
                    <FaFilePdf /> Télécharger PDF
                  </button>
                  <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
                </div>
              </div>
              <div className="subscriber-details">
                <section className="detail-section">
                  <h3>Identité (pour remplir le PDF)</h3>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Titolo:</label><span>{selected.title}</span></div>
                    <div className="detail-item"><label>Nome:</label><span>{selected.firstName}</span></div>
                    <div className="detail-item"><label>Cognome:</label><span>{selected.lastName}</span></div>
                    <div className="detail-item"><label>Sesso:</label><span>{selected.sex}</span></div>
                    <div className="detail-item">
                      <label>Data di nascita:</label>
                      <span>
                        {selected.dateOfBirth
                          ? formatBirthDateDisplayUtc(selected.dateOfBirth, 'fr-CH') || 'N/A'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item"><label>Luogo di nascita:</label><span>{selected.birthPlace}</span></div>
                    <div className="detail-item"><label>Stato di nascita:</label><span>{selected.birthCountry}</span></div>
                    <div className="detail-item"><label>Prov. nascita:</label><span>{selected.birthProvince || '–'}</span></div>
                  </div>
                </section>
                <section className="detail-section">
                  <h3>Documento d'identità</h3>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Tipologia:</label><span>{selected.idDocumentType}</span></div>
                    <div className="detail-item"><label>N.:</label><span>{selected.idNumber}</span></div>
                    <div className="detail-item"><label>Scadenza:</label><span>{formatDateOnly(selected.idExpiry)}</span></div>
                  </div>
                </section>
                <section className="detail-section">
                  <h3>Indirizzo di residenza</h3>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Indirizzo:</label><span>{selected.addressStreet}</span></div>
                    <div className="detail-item"><label>N° civ.:</label><span>{selected.addressStreetNumber}</span></div>
                    <div className="detail-item"><label>Città:</label><span>{selected.addressCity}</span></div>
                    <div className="detail-item"><label>CAP:</label><span>{selected.addressPostalCode}</span></div>
                    <div className="detail-item"><label>Prov.:</label><span>{selected.addressProvince || '–'}</span></div>
                    <div className="detail-item"><label>Stato:</label><span>{selected.addressCountry}</span></div>
                  </div>
                </section>
                <section className="detail-section">
                  <h3>Contatti</h3>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Telefono:</label><span>{formatPhoneDisplay(selected.phone)}</span></div>
                    <div className="detail-item"><label>Cellulare:</label><span>{selected.mobile || '–'}</span></div>
                    <div className="detail-item"><label>Email:</label><span>{selected.email}</span></div>
                  </div>
                </section>
                {selected.isMinor && (
                  <section className="detail-section">
                    <h3>Responsabile genitoriale (minore)</h3>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Nome:</label><span>{selected.parentFirstName}</span></div>
                      <div className="detail-item"><label>Cognome:</label><span>{selected.parentLastName}</span></div>
                      <div className="detail-item"><label>Luogo di nascita:</label><span>{selected.parentBirthPlace || '–'}</span></div>
                      <div className="detail-item">
                        <label>Data di nascita:</label>
                        <span>
                          {selected.parentDateOfBirth
                            ? formatBirthDateDisplayUtc(selected.parentDateOfBirth, 'fr-CH') || 'N/A'
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </section>
                )}
                <section className="detail-section">
                  <h3>Déclarations / Date</h3>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Luogo e data (firma):</label><span>{selected.signaturePlace || '–'}</span></div>
                    <div className="detail-item"><label>Date envoi:</label><span>{formatDate(selected.createdAt)}</span></div>
                    <div className="detail-item"><label>Langue formulaire:</label><span>{selected.formLanguage || 'fr'}</span></div>
                  </div>
                  {selected.signatureImage && (
                    <div className="detail-item" style={{ marginTop: '1rem' }}>
                      <label>Signature:</label>
                      <img src={selected.signatureImage} alt="Signature" style={{ maxWidth: '320px', border: '1px solid #ddd', borderRadius: 6 }} />
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminJuventusCard;
